import React, { useState, useEffect } from "react";
import {
  FaHome,
  FaFileInvoice,
  FaUserAlt,
  FaTools,
  FaChartBar,
  FaComments,
  FaUsers,
  FaEnvelope,
  FaBriefcase,
  FaClipboardList,
  FaBuilding,
  FaBars,
  FaCaretDown,
  FaTimes,
} from "react-icons/fa";
import { TbReportAnalytics } from "react-icons/tb";
import { GoCrossReference } from "react-icons/go";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../../assets/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { toggleSideBar } from "../../../state/reducers/applicationSlice";

const Sidebar = ({ isOpen, setisSideBarOpen }) => {
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const userRole = useSelector((state) => state.user.role);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showCommunications, setShowCommunications] = useState(false);
  const [showPostServices, setShowPostServices] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [activeCommLink, setActiveCommLink] = useState("home");
  const [activeToolsLink, setActiveToolsLink] = useState("services&pricing");
  const [activeReportLink, setActiveReportLink] = useState("leads-reports");

  // Check if screen is mobile or tablet
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const navItemBase =
    "flex items-center p-2.5 cursor-pointer rounded-xl transition-all duration-200";
  const navItemState = (active) =>
    active
      ? "bg-indigo-50 text-indigo-700 border border-indigo-100 shadow-sm"
      : "text-slate-600 hover:bg-slate-100";
  const subItemState = (active) =>
    active
      ? "bg-indigo-50 text-indigo-700 font-semibold border border-indigo-100"
      : "text-slate-600 hover:bg-slate-100";
  const iconClass = "text-xl text-slate-700";

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth < 768); // 768px is typical tablet breakpoint
    };

    // Initial check
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [window.innerWidth, window.innerHeight]);

  // Helper function to determine if a route is active
  const isActive = (path) => location.pathname.startsWith(path);

  // Toggle functions
  const handleCommunicationsToggle = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setShowCommunications(!showCommunications);
  };

  const handleReportsToggle = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setShowReports(!showReports);
  };

  const handlePostServicesToggle = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setShowPostServices(!showPostServices);
  };

  const handleToolsToggle = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    if (!isSideBarOpen) {
      // If sidebar is collapsed, expand it first and then show dropdown
      dispatch(toggleSideBar());
      setShowTools(true);
    } else {
      setShowTools(!showTools);
    }
  };

  // Function to handle clicking on Communications links
  const handleCommLinkClick = (link) => {
    setActiveCommLink(link);
    navigate(`/admin/communications/${link}`);
    if (isMobileOrTablet) {
      dispatch(toggleSideBar()); // Close sidebar after navigation on mobile
    }
  };

  const handleToolsLinkClick = (link) => {
    setActiveToolsLink(link);
    navigate(`/admin/tools/${link}`);
    if (isMobileOrTablet) {
      dispatch(toggleSideBar()); // Close sidebar after navigation on mobile
    }
  };

  const handlePostLinkClick = (link) => {
    setActiveCommLink(link);
    navigate(`/admin/postServices/${link}`);
    if (isMobileOrTablet) {
      dispatch(toggleSideBar()); // Close sidebar after navigation on mobile
    }
  };

  const handleReportsClick = (link) => {
    setActiveReportLink(link);
    navigate(`/admin/reports/${link}`);
    if (isMobileOrTablet) {
      dispatch(toggleSideBar()); // Close sidebar after navigation on mobile
    }
  };

  // Handle navigation with optional sidebar closing on mobile
  const handleNavigate = (path) => {
    navigate(path);
    if (isMobileOrTablet) {
      dispatch(toggleSideBar()); // Close sidebar after navigation on mobile
    }
  };

  return (
    <>
      {/* For mobile and tablet: Full screen overlay sidebar */}
      {isMobileOrTablet && (
        <div
          className={`fixed inset-0 z-[2000] bg-slate-950/45 backdrop-blur-sm transition-opacity duration-300 ${
            isSideBarOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className={`h-full w-[88%] max-w-sm bg-white shadow-2xl border-r border-slate-200 transition-transform duration-300 ${
              isSideBarOpen ? "translate-x-0" : "-translate-x-full"
            } overflow-y-auto`}
          >
          {/* Mobile Sidebar Header with Close Button */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <img src={logo} alt="Logo" className="w-32" />
            <button
              onClick={() => dispatch(toggleSideBar())}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 p-2 rounded-lg transition-colors"
              aria-label="Close sidebar"
            >
              <FaTimes size={24} />
            </button>
          </div>

          {/* Mobile Sidebar Content */}
          <div className="p-4">
            <h2 className="text-xs font-semibold mb-4 text-slate-400 tracking-[0.14em] uppercase">
              CRM
            </h2>
            <ul className="mt-4 space-y-4">
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/dashboard")
                )}`}
                onClick={() => handleNavigate("/admin/dashboard")}
              >
                <FaHome className={iconClass} />
                <span className="ml-4">Home</span>
              </li>

              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/application")
                )}`}
                onClick={() => handleNavigate("/admin/application")}
              >
                <FaClipboardList className={iconClass} />
                <span className="ml-4">Applications</span>
              </li>

              <li
                className={`${navItemBase} ${navItemState(isActive("/admin/leads"))}`}
                onClick={() => handleNavigate("/admin/leads")}
              >
                <FaUsers className={iconClass} />
                <span className="ml-4">Leads</span>
              </li>

              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/invoices")
                )}`}
                onClick={() => handleNavigate("/admin/invoices")}
              >
                <FaFileInvoice className={iconClass} />
                <span className="ml-4">Invoices</span>
              </li>

              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/customers")
                )}`}
                onClick={() => handleNavigate("/admin/customers")}
              >
                <FaUserAlt className={iconClass} />
                <span className="ml-4">Customers</span>
              </li>

              {/* Tools with dropdown */}
              <li
                className={`${navItemBase} ${navItemState(isActive("/admin/tools"))}`}
                onClick={handleToolsToggle}
              >
                <FaTools className={iconClass} />
                <div className="ml-4 flex items-center justify-between flex-grow">
                  <span>Tools</span>
                  <FaCaretDown
                    className={`text-sm transform transition-transform ${
                      showTools ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </li>

              {/* Tools Dropdown */}
              {showTools && (
                <ul className="pl-10 space-y-2 mt-2">
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeToolsLink === "services&pricing"
                    )}`}
                    onClick={() => handleToolsLinkClick("services&pricing")}
                  >
                    <span>Services & Pricing</span>
                  </li>
                  {userRole === "superadmin" && (
                    <li
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                        activeToolsLink === "assigner"
                      )}`}
                      onClick={() => handleToolsLinkClick("assigner")}
                    >
                      <span>Role Assigner</span>
                    </li>
                  )}
                </ul>
              )}

              {/* Reports for superadmin */}
              {userRole === "superadmin" && (
                <li
                  className={`${navItemBase} ${navItemState(
                    isActive("/admin/reports/leads-reports")
                  )}`}
                  onClick={() => handleNavigate("/admin/reports/leads-reports")}
                >
                  <FaChartBar className={iconClass} />
                  <span className="ml-4">Reports</span>
                </li>
              )}

              {/* Communications with Dropdown */}
              <li
                className={`${navItemBase} ${navItemState(false)}`}
                onClick={handleCommunicationsToggle}
              >
                <FaComments className={iconClass} />
                <div className="ml-4 flex items-center justify-between flex-grow">
                  <span>Communications</span>
                  <FaCaretDown
                    className={`text-sm transform transition-transform ${
                      showCommunications ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </li>

              {/* Communications Dropdown */}
              {showCommunications && (
                <ul className="pl-10 space-y-2 mt-2">
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "home"
                    )}`}
                    onClick={() => handleCommLinkClick("home")}
                  >
                    <span>Home</span>
                  </li>
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "contacts"
                    )}`}
                    onClick={() => handleCommLinkClick("contacts")}
                  >
                    <span>Contacts</span>
                  </li>
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "campaigns"
                    )}`}
                    onClick={() => handleCommLinkClick("campaigns")}
                  >
                    <span>Campaigns</span>
                  </li>
                </ul>
              )}

              {/* Post Services with Dropdown */}
              <li
                className={`${navItemBase} ${navItemState(false)}`}
                onClick={handlePostServicesToggle}
              >
                <FaEnvelope className={iconClass} />
                <div className="ml-4 flex items-center justify-between flex-grow">
                  <span>Post Services</span>
                  <FaCaretDown
                    className={`text-sm transform transition-transform ${
                      showPostServices ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </li>

              {/* Post Services Dropdown */}
              {showPostServices && (
                <ul className="pl-10 space-y-2 mt-2">
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "careercounciling"
                    )}`}
                    onClick={() => handlePostLinkClick("careercounciling")}
                  >
                    <span>Career Counseling</span>
                  </li>
                </ul>
              )}

              <li
                className={`${navItemBase} ${navItemState(false)}`}
                onClick={() => handleNavigate("/admin/resources")}
              >
                <FaBriefcase className={iconClass} />
                <span className="ml-4">Resources</span>
              </li>

              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/dataTable")
                )}`}
                onClick={() => handleNavigate("/admin/dataTable")}
              >
                <FaClipboardList className={iconClass} />
                <span className="ml-4">Application Data</span>
              </li>
            </ul>

            {/* HR Section */}
            <div className="mt-8">
              <h2 className="text-xs font-semibold mb-4 text-slate-400 tracking-[0.14em] uppercase">
                HR
              </h2>
              <ul>
                <li
                  className={`${navItemBase} ${navItemState(false)}`}
                  onClick={() => handleNavigate("/admin/leave-wfh")}
                >
                  <FaBuilding className={iconClass} />
                  <span className="ml-4">Leave/WFH</span>
                </li>
              </ul>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Desktop version - fixed to ensure icon clicks work in collapsed mode */}
      {!isMobileOrTablet && (
        <div
          className={`${
            isSideBarOpen ? "w-80" : "w-0 md:w-24"
          } fixed top-0 left-0 h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100 border-r border-slate-200 shadow-[0_14px_40px_-22px_rgba(15,23,42,0.45)] transition-width duration-300 ease-in-out flex flex-col overflow-y-auto`}
        >
          {/* Sidebar Content */}
          <div className="w-full mt-28 h-auto p-4">
            <h2
              className={`text-xs font-semibold mb-4 text-slate-400 tracking-[0.14em] uppercase ${
                !isSideBarOpen && "text-center"
              }`}
            >
              {isSideBarOpen ? "CRM" : ""}
            </h2>
            <ul className="mt-4 space-y-3">
              {/* Home */}
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/dashboard")
                )} ${!isSideBarOpen && "justify-center"}`}
                onClick={() => handleNavigate("/admin/dashboard")}
              >
                <FaHome
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Home</span>
                )}
              </li>

              {/* Applications */}
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/application")
                )} ${!isSideBarOpen && "justify-center"}`}
                onClick={() => handleNavigate("/admin/application")}
              >
                <FaClipboardList
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Applications</span>
                )}
              </li>

              {/* Leads */}
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/leads")
                )} ${!isSideBarOpen && "justify-center"}`}
                onClick={() => handleNavigate("/admin/leads")}
              >
                <FaUsers
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Leads</span>
                )}
              </li>

              {/* Invoices */}
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/invoices")
                )} ${!isSideBarOpen && "justify-center"}`}
                onClick={() => handleNavigate("/admin/invoices")}
              >
                <FaFileInvoice
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Invoices</span>
                )}
              </li>

              {/* Customers */}
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/customers")
                )} ${!isSideBarOpen && "justify-center"}`}
                onClick={() => handleNavigate("/admin/customers")}
              >
                <FaUserAlt
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Customers</span>
                )}
              </li>

              {/* Tools */}
              <li
                className={`${navItemBase} ${navItemState(isActive("/admin/tools"))} ${
                  !isSideBarOpen && "justify-center"
                }`}
                onClick={(e) => {
                  if (!isSideBarOpen) {
                    // If collapsed, just navigate to default tools page
                    handleNavigate("/admin/tools/services&pricing");
                  } else {
                    // If expanded, toggle dropdown
                    handleToolsToggle(e);
                  }
                }}
              >
                <FaTools
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <div className="ml-4 flex items-center justify-between flex-grow">
                    <span>Tools</span>
                    <FaCaretDown
                      className={`text-sm transform transition-transform ${
                        showTools ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                )}
              </li>

              {/* Tools Dropdown */}
              {showTools && isSideBarOpen && (
                <ul className="pl-6 space-y-2 mt-2">
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeToolsLink === "services&pricing"
                    )}`}
                    onClick={() => handleToolsLinkClick("services&pricing")}
                  >
                    <span className="ml-2">
                      Services & Pricing
                    </span>
                  </li>
                  {userRole === "superadmin" && (
                    <li
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                        activeToolsLink === "assigner"
                      )}`}
                      onClick={() => handleToolsLinkClick("assigner")}
                    >
                      <span className="ml-2">Role Assigner</span>
                    </li>
                  )}
                </ul>
              )}

              {/* Reports */}
              {userRole === "superadmin" && (
                <li
                  className={`${navItemBase} ${navItemState(
                    isActive("/admin/reports/leads-reports")
                  )} ${!isSideBarOpen && "justify-center"}`}
                  onClick={() => handleNavigate("/admin/reports/leads-reports")}
                >
                  <FaChartBar
                    className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                  />
                  {isSideBarOpen && (
                    <span className="ml-4">Reports</span>
                  )}
                </li>
              )}

              {/* Communications */}
              <li
                className={`${navItemBase} ${navItemState(false)} ${
                  !isSideBarOpen && "justify-center"
                }`}
                onClick={(e) => {
                  if (!isSideBarOpen) {
                    // If collapsed, just navigate to default communications page
                    handleNavigate("/admin/communications/home");
                  } else {
                    // If expanded, toggle dropdown
                    handleCommunicationsToggle(e);
                  }
                }}
              >
                <FaComments
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <div className="ml-4 flex items-center justify-between flex-grow">
                    <span>Communications</span>
                    <FaCaretDown
                      className={`text-sm transform transition-transform ${
                        showCommunications ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                )}
              </li>

              {/* Communications Dropdown */}
              {showCommunications && isSideBarOpen && (
                <ul className="pl-6 space-y-2 mt-2">
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "home"
                    )}`}
                    onClick={() => handleCommLinkClick("home")}
                  >
                    <span className="ml-2">Home</span>
                  </li>
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "contacts"
                    )}`}
                    onClick={() => handleCommLinkClick("contacts")}
                  >
                    <span className="ml-2">Contacts</span>
                  </li>
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "campaigns"
                    )}`}
                    onClick={() => handleCommLinkClick("campaigns")}
                  >
                    <span className="ml-2">Campaigns</span>
                  </li>
                </ul>
              )}

              {/* Post Services */}
              <li
                className={`${navItemBase} ${navItemState(false)} ${
                  !isSideBarOpen && "justify-center"
                }`}
                onClick={(e) => {
                  if (!isSideBarOpen) {
                    // If collapsed, just navigate to default post services page
                    handleNavigate("/admin/postServices/careercounciling");
                  } else {
                    // If expanded, toggle dropdown
                    handlePostServicesToggle(e);
                  }
                }}
              >
                <FaEnvelope
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <div className="ml-4 flex items-center justify-between flex-grow">
                    <span>Post Services</span>
                    <FaCaretDown
                      className={`text-sm transform transition-transform ${
                        showPostServices ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                )}
              </li>

              {/* Post Services Dropdown */}
              {showPostServices && isSideBarOpen && (
                <ul className="pl-6 space-y-2 mt-2">
                  <li
                    className={`flex items-center p-2 rounded-lg cursor-pointer ${subItemState(
                      activeCommLink === "careercounciling"
                    )}`}
                    onClick={() => handlePostLinkClick("careercounciling")}
                  >
                    <span className="ml-2">
                      Career Counseling
                    </span>
                  </li>
                </ul>
              )}

              {/* Resources */}
              <li
                className={`${navItemBase} ${navItemState(false)} ${
                  !isSideBarOpen && "justify-center"
                }`}
                onClick={() => handleNavigate("/admin/resources")}
              >
                <FaBriefcase
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Resources</span>
                )}
              </li>

              {/* Application Data */}
              <li
                className={`${navItemBase} ${navItemState(
                  isActive("/admin/dataTable")
                )} ${!isSideBarOpen && "justify-center"}`}
                onClick={() => handleNavigate("/admin/dataTable")}
              >
                <FaClipboardList
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Application Data</span>
                )}
              </li>
            </ul>
          </div>

          {/* HR Section */}
          <div
            className={`mt-8 p-4 ${
              !isSideBarOpen && "flex flex-col items-center"
            }`}
          >
            <h2 className="text-xs font-semibold mb-4 text-slate-400 tracking-[0.14em] uppercase">
              {isSideBarOpen ? "HR" : ""}
            </h2>
            <ul>
              <li
                className={`${navItemBase} ${navItemState(false)} ${
                  !isSideBarOpen && "justify-center"
                }`}
                onClick={() => handleNavigate("/admin/leave-wfh")}
              >
                <FaBuilding
                  className={`${iconClass} ${!isSideBarOpen ? "text-slate-700" : ""}`}
                />
                {isSideBarOpen && (
                  <span className="ml-4">Leave/WFH</span>
                )}
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
